import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { ADMIN_DOOR_WORDING } from '@/lib/admin-labels';
import { Eye, EyeOff, Info } from 'lucide-react';
import { type FormEvent, useState } from 'react';

/**
 * The door to every administration screen (auth Tahiti spec §6; C10 for the
 * one-door rule it keeps).
 *
 * It used to be one field holding a shared secret. It is now an ACCOUNT: the
 * eight people of the team enter with the address and the password they
 * already use, and the shared token stops being something a human knows (A-5).
 *
 * The backend is still the guard, not this form - it answers 401 whatever the
 * screen shows. This only spares an operator a blank page, and it is the one
 * place in the product where a password is typed, which is why the two fields
 * carry real `autoComplete` values: eight people open this screen every day,
 * and a password manager that cannot fill it teaches them to keep the password
 * somewhere it can reach.
 *
 * ## The shape came from `words-interface`, on purpose (issue #35)
 *
 * This was a bare `max-w-md` column of labels on white. The card of
 * `matrice.patrickmathieu.net` (`words-interface`, BUILD-352) is the form the
 * park settled on for a door: a grey ground, one white card, the logo INSIDE
 * it, the heading and its sentence centred, shell-framed fields, a
 * full-width button. Two doors of a park that shares one charte - the same
 * tokens, the same Titillium, the same warm greys - had no reason to look like
 * two products, and the difference was in the code rather than in anybody's
 * taste.
 *
 * What was NOT taken from the reference, and each omission is a decision:
 *
 * - **No « se souvenir de moi ».** Both sibling projects ship that checkbox and
 *   NEITHER ever reads its state: a control that does nothing is a lie told by
 *   an interface, and the cookie already outlives the tab on its own.
 * - **No « mot de passe oublié ? »**. It is wired to `() => undefined` in the
 *   reference, and Tahiti exposes no reset page this repository knows of. Same
 *   rule as the checkbox.
 * - **Bleu-nuit, never black.** The reference paints its button, its focus ring
 *   and its alert rule in pure black. Here the accent of the app IS `bleu-nuit`
 *   (charte §03, level 2), `Button` is the one spelling of a primary action, and
 *   `design-system-adherence.test.ts` refuses a raw hex outright - it caught the
 *   hex this very line used to quote, which is the guard doing its job on a
 *   comment rather than on a style.
 * - **The help line stays a sentence**, not a link. Wording and reasons in
 *   `lib/admin-labels.ts`.
 *
 * ## The notice is a `Callout`, and it is still an `<output>`
 *
 * It was a bare `<output>` carrying nothing but a sentence. `Callout` is the ONE
 * boxed sentence of the administration screens - its own header argues at length
 * against a second spelling - and it renders an `<output>` when the role is
 * `status`, which issue #35 is what made possible. Nothing is traded away: the
 * element is the same, it just came with a box.
 *
 * POLITE and not an alert, which is the part that matters: the same slot carries
 * a refusal, an expiry and an outage, and only one of the three is an error.
 * Dressing « votre session a expiré » as something that went wrong sends an
 * operator checking a password that was right all along. `tone="neutral"` says
 * the same thing in colour - the box has nothing to signal, so it borrows no
 * noyau, and the charte holds this app at level 2 anyway.
 *
 * ONE LIMITATION, named rather than papered over. The region is mounted at the
 * same moment as its content, and a live region has to exist in the DOM BEFORE
 * its content changes for a screen reader to reliably announce it - so the
 * FIRST notice of a page load is likely to be read only when focus reaches it.
 * That behaviour is inherited, not introduced (the `<output>` this replaces was
 * conditional too, and `login-card.test.tsx` asserts the silence), and fixing
 * it means a permanently mounted region whose box appears later, which is a
 * change to how every notice of the product is built rather than to this card.
 */

export interface LoginCardProps {
  title: string;
  /** The wording of the button - « Ouvrir le journal », « Ouvrir les modèles ». */
  action: string;
  /**
   * What the door has to say, already worded (`lib/admin-labels.ts`), or `null`
   * for silence. A string rather than the old boolean because there are now
   * three things to say and one of them - « votre session a expiré » - is not a
   * refusal and must not be read as one.
   */
  notice?: string | null;
  /** Closes the button while the round trip to Tahiti is in flight. */
  submitting?: boolean;
  onSubmit: (email: string, password: string) => void;
}

/**
 * The shell a field is framed by, drawn once for both of them.
 *
 * `h-11` and not the reference's 46 px: 44 px is the minimum touch target of
 * WCAG 2.5.5 and the height every control of this repository already stands at
 * (`Button`, size `default`). An off-scale number here would be a third height
 * on a card that has two.
 *
 * The focus lives on the SHELL and not on the input, which is why the input
 * below suppresses its own `focus-visible` outline: the global rule in
 * `styles.css` would otherwise draw a ring inside a border that is already
 * changing colour, on a control the eye reads as one object.
 */
/*
  TWO pixels of border, and the focus is a COLOUR CHANGE on it - not a halo.
  The first draft of this card drew a 1 px border plus
  `focus-within:ring-2 ring-bleu-nuit-tint`, copying the reference's soft glow.
  Measured, `bleu-nuit-tint` on the white card is 1.20:1: the ring contributed
  nothing an eye can see, and the whole indicator had become a 1 px line where
  the door used to carry 2. So the border keeps the weight it had.

  `has-[input:focus-visible]:` and not `focus-within:`, which is precise rather
  than pedantic: the eye is INSIDE this shell, so `focus-within` lit the
  password wrapper up while the browser drew its own outline on the button -
  two things looking focused at once, and neither of them the field.
*/
const FIELD_SHELL =
  'flex h-11 items-center rounded-md border-2 border-gray-300 bg-white transition-colors duration-150 has-[input:focus-visible]:border-bleu-nuit';

/*
  `gray-600` and not the reference's pale grey: `gray-400` on white measures
  2.93:1, under the 4.5:1 of WCAG 1.4.3, and `font-light` on 16 px made it
  worse. `gray-600` is 4.55:1 - the same value `components/ui/button.tsx`
  settled on for the label of a disabled control, and for the same reason.
*/
const FIELD_INPUT =
  'h-full min-w-0 flex-1 bg-transparent px-3.5 text-base text-gray-900 placeholder:text-gray-600 focus-visible:outline-none';

const FIELD_LABEL = 'block font-semibold text-caption text-gray-800';

export function LoginCard({
  title,
  action,
  notice = null,
  submitting = false,
  onSubmit,
}: LoginCardProps) {
  /**
   * Whether the password is readable. UI state and not field state, which is
   * why it is the one `useState` on a form the repository otherwise keeps
   * uncontrolled: the value is read from the `FormData` at submission like
   * every other form here.
   */
  const [revealed, setRevealed] = useState(false);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    /*
      The address is trimmed and the password is NOT. A leading space in an
      address is a copy-paste artefact; a leading space in a password is a
      character somebody chose, and eating it would refuse a correct password
      while telling them it was wrong.
    */
    onSubmit(String(form.get('adminEmail') ?? '').trim(), String(form.get('adminPassword') ?? ''));
  }

  return (
    <>
      {/*
        44 px of side padding is the reference's, and it is held from `sm` up
        only. On a 390 px screen the card is 358 px wide, so 44 px on each side
        leaves 270 px of content - the fields go narrow and the 252 px logo
        touches both edges. 24 px below `sm` gives them 310 px, and nothing on a
        desktop moves.
      */}
      <div
        data-testid="porte-carte"
        className="w-full rounded-lg bg-white px-6 pt-10 pb-8 shadow-lg sm:px-11 sm:pt-12 sm:pb-10"
      >
        {/*
          Colour logo on white - the default pairing of the charte (§02), and
          252 px is the width the reference card gives it. It is the only logo
          on the screen now: the scene around this card paints none, because a
          brand shown twice on one page reads as two brands.
        */}
        <img
          src="/brand/logo-horizontal-transparent.png"
          alt="patrickmathieu singularité"
          width={252}
          height={39}
          className="mx-auto mb-7 block h-auto w-[252px] max-w-full"
        />
        {/*
          The heading names the DESTINATION, read from the door catalogue
          (`lib/admin-nav.ts`) - which is what lets the e2e specs keep clicking
          the button by its name. The sentence under it says what the card is.
        */}
        <h1 className="text-center font-bold text-h3 text-noir">{title}</h1>
        <p className="mt-1 text-center font-light text-gray-600 text-sm">
          {ADMIN_DOOR_WORDING.subtitle}
        </p>
        {notice === null ? null : (
          <Callout tone="neutral" live="status" className="mt-6 font-semibold">
            {/*
              `Info` and NOT a circled exclamation, which is where the first
              draft of this card contradicted itself: it spent six lines
              arguing that this slot must not read as an error, then drew an
              alert glyph beside all three messages. An operator whose session
              simply lapsed would see a warning sign next to « votre session a
              expiré » and go re-check a password that was right.
            */}
            <span className="flex items-start gap-2">
              <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-bleu-nuit" />
              <span>{notice}</span>
            </span>
          </Callout>
        )}
        <form onSubmit={submit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="adminEmail" className={FIELD_LABEL}>
              {ADMIN_DOOR_WORDING.emailLabel}
            </label>
            <div className={FIELD_SHELL}>
              <input
                id="adminEmail"
                name="adminEmail"
                type="email"
                autoComplete="username"
                placeholder={ADMIN_DOOR_WORDING.emailPlaceholder}
                required
                className={FIELD_INPUT}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="adminPassword" className={FIELD_LABEL}>
              {ADMIN_DOOR_WORDING.passwordLabel}
            </label>
            <div className={FIELD_SHELL}>
              <input
                id="adminPassword"
                name="adminPassword"
                type={revealed ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className={FIELD_INPUT}
              />
              {/*
                The eye. Its name is visually hidden TEXT and never an
                `aria-label` - the reason is in `lib/admin-labels.ts`, and it is
                the difference between this card and two broken test suites.

                `type="button"`, or it would submit the form it sits in.
              */}
              <button
                type="button"
                onClick={() => setRevealed((shown) => !shown)}
                className="flex h-full w-11 shrink-0 cursor-pointer items-center justify-center text-gray-500 transition-colors duration-150 hover:text-gray-900"
              >
                <span className="sr-only">
                  {revealed ? ADMIN_DOOR_WORDING.hidePassword : ADMIN_DOOR_WORDING.showPassword}
                </span>
                {revealed ? (
                  <EyeOff aria-hidden="true" className="size-5" />
                ) : (
                  <Eye aria-hidden="true" className="size-5" />
                )}
              </button>
            </div>
          </div>
          {/*
            Full width, and 48 px rather than the 44 px of every other button of
            the product: it is the only action on the screen, and the reference
            card draws it that way.
          */}
          <Button type="submit" disabled={submitting} className="h-12 w-full">
            {submitting ? ADMIN_DOOR_WORDING.submitting : action}
          </Button>
        </form>
      </div>
      {/*
        Outside the card, like the reference: it is about the door, not in it.

        `gray-700` rather than the reference's pale grey, because this line does
        NOT sit on white - it sits on the grey scene. `gray-500` there measures
        3.20:1 at 13 px, and this is the only sentence telling somebody who
        cannot get in what to do next.
      */}
      <p className="mt-6 text-center text-caption text-gray-700">{ADMIN_DOOR_WORDING.help}</p>
    </>
  );
}
